import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, ChangeDetectionStrategy, ChangeDetectorRef } from "@angular/core";
import { DriverViewModel, DriverNote } from "@app/shared/models/drivers/DriverViewModel.model";
import { MessageService } from "primeng/api";
import { authenticationService } from "@app/login/auth.service";
import { DriverService } from "@app/driver/shared/driver.service";
import { clientService } from "@app/client/shared/client.service";
import { ConfigService } from "@app/utilities/config/shared/config.service";
import { MaskService } from "@app/shared/services/mask/mask.service";
import { FeatureFlagsService } from "@app/agilx/shared/feature-flags.service";

@Component({
  selector: "app-driver-note-form-modal",
  templateUrl: "./driver-note-form-modal.component.html",
  styleUrls: ["./driver-note-form-modal.component.css"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DriverNoteFormModalComponent implements OnChanges {
  @Input() visible: boolean = false;
  @Input() note: Partial<DriverNote> = {};
  @Input() driver: DriverViewModel | null = null;
  @Input() readOnly: boolean = false;
  @Input() hideEmailButtons: boolean = false;
  dateInputMask: any;

  // Dropdown data
  @Input() tractors: any[] = [];
  @Input() trailers: any[] = [];
  @Input() clients: any[] = [];
  @Input() allClients: any[] = [];
  @Input() products: any[] = [];
  @Input() states: any[] = [];

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() saved = new EventEmitter<DriverNote>();
  @Output() closed = new EventEmitter<void>();

  noteTypes: string[] = [
    "General",
    "Driver Release",
    "Delays",
    "Breakdown & follow up",
    "Accident",
    "Incident",
  ];

  truckTrailerIssueOptions: { label: string; value: string }[] = [
    { label: "Truck", value: "Truck" },
    { label: "Trailer", value: "Trailer" },
    { label: "Both", value: "Both" },
  ];

  editNote: Partial<DriverNote> = {};
  filteredBillTos: any[] = [];
  attemptedSubmit: boolean = false;
  previousNoteType: string = "";

  // Weather autocomplete
  allWeatherValues: string[] = [];
  filteredWeatherValues: string[] = [];

  // Email preview states
  showEmailPreview: boolean = false;
  currentEmailType: "toSafety" | "formToDriver" | "toClient" | "toDriver" | null = null;
  emailPreview: {
    to: string;
    ccEmails: string;
    subject: string;
    body: string;
  } = { to: "", ccEmails: "", subject: "", body: "" };
  emailAttachments: File[] = [];
  emailValidationErrors: { to: string; cc: string } = { to: "", cc: "" };

  // Email states (for button indicators in footer)
  emailingToSafety: boolean = false;
  emailingFormToDriver: boolean = false;
  emailingToClient: boolean = false;
  emailingToDriver: boolean = false;

  driverNotesCcEmail: string = "omahadispatch@lgttransport.com";

  get emailFeaturesEnabled(): boolean {
    return this.featureFlags.isEnabled("emailFeatures");
  }

  constructor(
    private messageService: MessageService,
    private authService: authenticationService,
    private driverService: DriverService,
    private clientServiceRef: clientService,
    private configService: ConfigService,
    private maskService: MaskService,
    private cdr: ChangeDetectorRef,
    private featureFlags: FeatureFlagsService
  ) {
    this.dateInputMask = this.maskService.getInputDateOnlyMask();
    this.configService.getDriverNotesCcEmail().subscribe(
      (ccEmail: string) => {
        this.driverNotesCcEmail = ccEmail;
        this.cdr.markForCheck();
      },
      () => {}
    );
    this.driverService.getWeatherSuggestions().subscribe((data: string[]) => {
      this.allWeatherValues = data || [];
      this.cdr.markForCheck();
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes["visible"] && this.visible) {
      this.editNote = { ...this.note };
      this.attemptedSubmit = false;
      this.previousNoteType = this.note.type || "";
      if (this.editNote.followUpDate) {
        const d = new Date(this.editNote.followUpDate as any);
        if (!isNaN(d.getTime())) {
          const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
          const dd = String(d.getUTCDate()).padStart(2, "0");
          const yyyy = d.getUTCFullYear();
          (this.editNote as any).followUpDate = `${mm}/${dd}/${yyyy}`;
        }
      }
      if (this.editNote.clientId) {
        this.onClientChange();
      }
      this.cdr.markForCheck();
    }
  }

  get dialogHeader(): string {
    if (this.readOnly) return "View Note";
    return this.editNote.id ? "Edit Note" : "Add Note";
  }

  closeModal() {
    this.visible = false;
    this.visibleChange.emit(false);
    this.closed.emit();
  }

  saveNote() {
    this.attemptedSubmit = true;

    if (!this.editNote.type) {
      this.scrollToFirstError();
      return;
    }

    if (this.isAccidentOrIncident(this.editNote.type)) {
      if (
        this.editNote.anyInjuries === undefined ||
        this.editNote.anyInjuries === null ||
        this.editNote.anotherVehicleInvolved === undefined ||
        this.editNote.anotherVehicleInvolved === null ||
        this.editNote.driverCitation === undefined ||
        this.editNote.driverCitation === null ||
        !this.editNote.tractorId ||
        !this.editNote.trailerId ||
        !this.editNote.loadedEmpty ||
        !this.editNote.productId ||
        !this.editNote.accidentDate ||
        !this.editNote.accidentTime ||
        !this.editNote.accidentCity ||
        !this.editNote.accidentState ||
        !this.editNote.accidentLocation ||
        !this.editNote.weather ||
        !this.editNote.accidentDescription ||
        !this.editNote.damageDescription ||
        !this.editNote.assignedCustomer ||
        !this.editNote.photosToSafety ||
        (this.editNote.driverCitation && !this.editNote.citationDescription)
      ) {
        this.scrollToFirstError();
        return;
      }
    } else if (this.isDelayOrBreakdown(this.editNote.type)) {
      if (
        !this.editNote.clientId ||
        !this.editNote.billToId ||
        !this.editNote.loadedEmpty ||
        !this.editNote.eta ||
        !this.editNote.clientNotes ||
        (this.isBreakdown(this.editNote.type) && !this.editNote.roadCallNumber) ||
        (this.isBreakdown(this.editNote.type) && !this.editNote.truckTrailerIssue)
      ) {
        this.scrollToFirstError();
        return;
      }
    } else if (this.isDriverRelease(this.editNote.type)) {
      if (
        !this.editNote.driverLocation ||
        !this.editNote.loadedEmpty ||
        this.editNote.requestTimeOff === undefined ||
        this.editNote.requestTimeOff === null ||
        !this.editNote.trailerDropoffLocation ||
        !this.editNote.nextAvailable
      ) {
        this.scrollToFirstError();
        return;
      }
    } else {
      if (!this.editNote.note) {
        this.scrollToFirstError();
        return;
      }
    }

    // Populate display names
    if (this.editNote.tractorId) {
      const tractor = this.tractors.find((t: any) => t.id === this.editNote.tractorId);
      this.editNote.tractorNumber = tractor?.unitNumber || tractor?.itemName;
    }
    if (this.editNote.trailerId) {
      const trailer = this.trailers.find((t: any) => t.id === this.editNote.trailerId);
      this.editNote.trailerNumber = trailer?.trailerNo;
    }
    if (this.editNote.clientId) {
      const client = this.clients.find((c: any) => c.id === this.editNote.clientId);
      this.editNote.clientName = client?.name;
    }
    if (this.editNote.billToId) {
      const billTo = this.filteredBillTos.find((c: any) => c.id === this.editNote.billToId);
      this.editNote.billToName = billTo?.name || billTo?.companyName;
    }
    if (this.editNote.productId) {
      const product = this.products.find((p: any) => p.id === this.editNote.productId);
      this.editNote.productName = product?.name;
    }

    if (!this.editNote.id) {
      this.editNote.createdByUserName = this.authService.getUsername() ?? undefined;
      this.editNote.dateCreatedUtc = new Date();
    }

    this.editNote.followUpDate = this.editNote.followUpDate
      ? new Date(new Date(this.editNote.followUpDate).toISOString())
      : undefined;

    this.saved.emit(this.editNote as DriverNote);
  }

  onNoteTypeChange() {
    const currentType = this.editNote.type || "";
    const previousType = this.previousNoteType;

    const wasAccidentOrIncident = this.isAccidentOrIncident(previousType);
    const isAccidentOrIncidentNow = this.isAccidentOrIncident(currentType);
    const wasDelayOrBreakdown = this.isDelayOrBreakdown(previousType);
    const isDelayOrBreakdownNow = this.isDelayOrBreakdown(currentType);

    const formTypeChanged =
      wasAccidentOrIncident !== isAccidentOrIncidentNow ||
      wasDelayOrBreakdown !== isDelayOrBreakdownNow;

    if (formTypeChanged) {
      this.editNote.anyInjuries = undefined;
      this.editNote.anotherVehicleInvolved = undefined;
      this.editNote.driverCitation = undefined;
      this.editNote.citationDescription = undefined;
      this.editNote.tractorId = undefined;
      this.editNote.trailerId = undefined;
      this.editNote.loadedEmpty = undefined;
      this.editNote.productId = undefined;
      this.editNote.accidentDate = undefined;
      this.editNote.accidentTime = undefined;
      this.editNote.accidentCity = undefined;
      this.editNote.accidentState = undefined;
      this.editNote.accidentLocation = undefined;
      this.editNote.weather = undefined;
      this.editNote.accidentDescription = undefined;
      this.editNote.damageDescription = undefined;
      this.editNote.assignedCustomer = undefined;
      this.editNote.photosToSafety = undefined;
      this.editNote.clientId = undefined;
      this.editNote.billToId = undefined;
      this.editNote.tripNumber = undefined;
      this.editNote.hosRemaining = undefined;
      this.editNote.internalNotes = undefined;
      this.editNote.clientNotes = undefined;
      this.editNote.roadCallNumber = undefined;
      this.editNote.customerReferenceNumber = undefined;
      this.editNote.eta = undefined;
      this.editNote.truckTrailerIssue = undefined;
      this.filteredBillTos = [];

      if (isAccidentOrIncidentNow && this.driver) {
        this.editNote.tractorId = this.driver.tractorId;
        this.editNote.trailerId = this.driver.trailerId;
      } else if (isDelayOrBreakdownNow && this.driver) {
        this.editNote.clientId = this.driver.clientId;
        this.editNote.billToId = this.driver.billToId;
        if (this.editNote.clientId) {
          this.onClientChange();
        }
      }
    }

    this.previousNoteType = currentType;
  }

  onClientChange() {
    if (this.editNote.clientId) {
      this.filteredBillTos = this.allClients.filter(
        (c: any) => c.clientId === this.editNote.clientId
      );
    } else {
      this.filteredBillTos = [];
    }
    if (
      this.editNote.billToId &&
      !this.filteredBillTos.some((b: any) => b.id === this.editNote.billToId)
    ) {
      this.editNote.billToId = undefined;
    }
  }

  filterWeatherValues(event: any) {
    const query = event.query.toLowerCase();
    this.filteredWeatherValues = this.allWeatherValues.filter((w: string) =>
      w.toLowerCase().includes(query)
    );
    if (query && !this.filteredWeatherValues.some((w: string) => w.toLowerCase() === query)) {
      this.filteredWeatherValues.push(event.query);
    }
  }

  // Email methods
  emailToSafety() {
    if (!this.editNote?.id || !this.driver) return;
    const driverName = `${this.driver.name} ${this.driver.lastName}`;
    const noteDetails = this.buildAccidentNoteDetails(this.editNote as DriverNote);
    this.emailPreview = {
      to: "safety@lgttransport.com",
      ccEmails: this.driverNotesCcEmail,
      subject: `${this.editNote.type} Report - Driver ${driverName}`,
      body: `Driver: ${driverName}\n\n${noteDetails}`,
    };
    this.currentEmailType = "toSafety";
    this.showEmailPreview = true;
  }

  emailFormToDriver() {
    if (!this.editNote?.id || !this.driver) return;
    const driverName = `${this.driver.name} ${this.driver.lastName}`;
    const formLink =
      "https://docs.google.com/forms/d/e/1FAIpQLSecnRaMbaTlvEyjgeHzM2Qo4tDFfFAjNisgxSDpl8K4SfPPsw/viewform";
    this.emailPreview = {
      to: this.driver.email || "",
      ccEmails: this.driverNotesCcEmail,
      subject: `${this.editNote.type} Form Required - ${driverName}`,
      body: `${driverName},\n\nPlease complete the following ${this.editNote.type?.toLowerCase()} form:\n\n${formLink}\n\nThank you.`,
    };
    this.currentEmailType = "formToDriver";
    this.showEmailPreview = true;
  }

  emailToClient() {
    if (!this.editNote?.id || !this.driver) return;
    if (this.editNote.billToId) {
      this.clientServiceRef.getClientInfo(this.editNote.billToId.toString()).subscribe(
        (billTo: any) => {
          this.openEmailToClientPreview(billTo?.email || billTo?.invoiceEmail || "");
          this.cdr.markForCheck();
        },
        () => {
          this.openEmailToClientPreview("");
          this.cdr.markForCheck();
        }
      );
    } else {
      this.openEmailToClientPreview("");
    }
  }

  private openEmailToClientPreview(clientEmail: string) {
    const driverName = `${this.driver!.name} ${this.driver!.lastName}`;
    const delayDetails = this.buildDelayNoteDetails(this.editNote as DriverNote);
    const noteTypeForMessage = this.editNote.type?.toLowerCase().includes("delay")
      ? "delay"
      : "breakdown";
    this.emailPreview = {
      to: clientEmail,
      ccEmails: this.driverNotesCcEmail,
      subject: `${this.editNote.type} Notification - Trip ${this.editNote.tripNumber || "N/A"}`,
      body: `We wanted to inform you of a ${noteTypeForMessage}:\n\nDriver: ${driverName}\nTrip: ${
        this.editNote.tripNumber || "N/A"
      }\n\n${delayDetails}\n\nThank you for your understanding.`,
    };
    this.currentEmailType = "toClient";
    this.showEmailPreview = true;
  }

  emailToDriver() {
    if (!this.editNote?.id || !this.driver) return;
    const driverName = `${this.driver.name} ${this.driver.lastName}`;
    const delayDetails = this.buildDelayNoteDetails(this.editNote as DriverNote);
    this.emailPreview = {
      to: this.driver.email || "",
      ccEmails: this.driverNotesCcEmail,
      subject: `${this.editNote.type} Notification - Trip ${this.editNote.tripNumber || "N/A"}`,
      body: `${driverName},\n\nThis is a notification regarding your current trip:\n\nTrip Number: ${
        this.editNote.tripNumber || "N/A"
      }\n\n${delayDetails}\n\nPlease contact dispatch if you have any questions.\n\nThank you.`,
    };
    this.currentEmailType = "toDriver";
    this.showEmailPreview = true;
  }

  confirmSendEmail() {
    if (!this.validateEmailFields()) return;
    if (!this.editNote?.id) return;

    const sendFn = (emailType: string) => {
      this.driverService
        .sendCustomEmail(
          this.editNote.id!,
          this.emailPreview.to,
          this.emailPreview.subject,
          this.emailPreview.body,
          emailType,
          this.emailPreview.ccEmails,
          this.emailAttachments
        )
        .subscribe(
          (response: any) => {
            this.showEmailPreview = false;
            this.emailAttachments = [];
            if (response.note) {
              Object.assign(this.editNote, response.note);
            }
            this.messageService.add({
              severity: "success",
              summary: "Email Sent",
              detail: `Email has been sent to ${this.emailPreview.to}`,
            });
            this.cdr.markForCheck();
          },
          (error: any) => {
            this.messageService.add({
              severity: "error",
              summary: "Error",
              detail: error.error?.error || "Failed to send email. Please try again.",
            });
            this.cdr.markForCheck();
          }
        );
    };

    switch (this.currentEmailType) {
      case "toSafety":
        sendFn("toSafety");
        break;
      case "formToDriver":
        sendFn("formToDriver");
        break;
      case "toClient":
        sendFn("toClient");
        break;
      case "toDriver":
        sendFn("toDriver");
        break;
    }
  }

  cancelEmailPreview() {
    this.showEmailPreview = false;
    this.currentEmailType = null;
    this.emailValidationErrors = { to: "", cc: "" };
    this.emailAttachments = [];
  }

  onFilesSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      for (let i = 0; i < input.files.length; i++) {
        const file = input.files[i];
        if (file.type.startsWith("image/")) {
          this.emailAttachments.push(file);
        }
      }
    }
    input.value = "";
  }

  removeAttachment(index: number) {
    this.emailAttachments.splice(index, 1);
  }

  formatFileSize(bytes: number): string {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  }

  clearEmailValidationError(field: "to" | "cc") {
    this.emailValidationErrors[field] = "";
  }

  isValidEmail(email: string): boolean {
    if (!email || !email.trim()) return false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  }

  validateEmailFields(): boolean {
    this.emailValidationErrors = { to: "", cc: "" };
    let isValid = true;
    if (!this.emailPreview.to || !this.emailPreview.to.trim()) {
      this.emailValidationErrors.to = "Recipient email is required.";
      isValid = false;
    } else if (!this.isValidEmail(this.emailPreview.to)) {
      this.emailValidationErrors.to = "Please enter a valid email address.";
      isValid = false;
    }
    if (this.emailPreview.ccEmails && this.emailPreview.ccEmails.trim()) {
      const ccEmails = this.emailPreview.ccEmails
        .split(/[;,]/)
        .map((e: string) => e.trim())
        .filter((e: string) => e);
      for (const cc of ccEmails) {
        if (!this.isValidEmail(cc)) {
          this.emailValidationErrors.cc = `Invalid email: ${cc}`;
          isValid = false;
          break;
        }
      }
    }
    return isValid;
  }

  buildAccidentNoteDetails(note: DriverNote): string {
    let details = [];
    if (note.tractorNumber) details.push(`Tractor: ${note.tractorNumber.toUpperCase()}`);
    if (note.trailerNumber) details.push(`Trailer: ${note.trailerNumber.toUpperCase()}`);
    if (note.loadedEmpty) details.push(`Loaded/Empty: ${note.loadedEmpty.toUpperCase()}`);
    if (note.productName) details.push(`Product: ${note.productName.toUpperCase()}`);
    if (note.accidentDate) details.push(`Date: ${note.accidentDate}`);
    if (note.accidentTime) details.push(`Time: ${note.accidentTime}`);
    if (note.accidentCity || note.accidentState)
      details.push(
        `Location: ${(note.accidentCity || "").toUpperCase()}, ${(note.accidentState || "").toUpperCase()}`
      );
    if (note.weather) details.push(`Weather: ${note.weather.toUpperCase()}`);
    if (note.accidentLocation)
      details.push(`Accident Location: ${note.accidentLocation.toUpperCase()}`);
    if (note.accidentDescription)
      details.push(`Description: ${note.accidentDescription.toUpperCase()}`);
    if (note.damageDescription) details.push(`Damage: ${note.damageDescription.toUpperCase()}`);
    if (note.anyInjuries !== undefined)
      details.push(`Injuries/Tow-aways/Fatalities: ${note.anyInjuries ? "YES" : "NO"}`);
    if (note.anotherVehicleInvolved !== undefined)
      details.push(`Another Vehicle: ${note.anotherVehicleInvolved ? "YES" : "NO"}`);
    if (note.driverCitation !== undefined)
      details.push(`Citation: ${note.driverCitation ? "YES" : "NO"}`);
    if (note.citationDescription)
      details.push(`Citation Details: ${note.citationDescription.toUpperCase()}`);
    if (note.assignedCustomer)
      details.push(`Assigned Customer: ${note.assignedCustomer.toUpperCase()}`);
    if (note.photosToSafety) details.push(`Photos to Safety: ${note.photosToSafety.toUpperCase()}`);
    if (note.note) details.push(`Notes: ${note.note}`);
    return details.join("\n");
  }

  buildDelayNoteDetails(note: DriverNote): string {
    let details = [];
    if (note.tripNumber) details.push(`Trip Number: ${note.tripNumber}`);
    if (note.customerReferenceNumber)
      details.push(`Customer Reference: ${note.customerReferenceNumber}`);
    if (note.eta) details.push(`ETA: ${note.eta}`);
    if (note.truckTrailerIssue) details.push(`Issue Type: ${note.truckTrailerIssue}`);
    if (note.loadedEmpty) details.push(`Loaded/Empty: ${note.loadedEmpty.toUpperCase()}`);
    if (note.productName) details.push(`Product: ${note.productName.toUpperCase()}`);
    if (note.hosRemaining !== undefined) details.push(`HOS Remaining: ${note.hosRemaining} hours`);
    if (note.clientNotes) details.push(`Client Notes: ${note.clientNotes.toUpperCase()}`);
    return details.join("\n");
  }

  isAccidentOrIncident(type?: string): boolean {
    return type === "Accident" || type === "Incident";
  }

  isDelayOrBreakdown(type?: string): boolean {
    return type === "Delays" || type === "Breakdown & follow up";
  }

  isBreakdown(type?: string): boolean {
    return type === "Breakdown & follow up";
  }

  isDriverRelease(type?: string): boolean {
    return type === "Driver Release";
  }

  private scrollToFirstError() {
    setTimeout(() => {
      const modalBody = document.querySelector(".note-form-dialog .modal-body");
      if (!modalBody) return;
      const firstError = modalBody.querySelector(".is-invalid, .form-control.ng-invalid");
      if (firstError) {
        const errorPosition = (firstError as HTMLElement).offsetTop;
        modalBody.scrollTo({ top: errorPosition - 20, behavior: "smooth" });
        if (
          firstError instanceof HTMLElement &&
          (firstError.tagName === "INPUT" ||
            firstError.tagName === "SELECT" ||
            firstError.tagName === "TEXTAREA")
        ) {
          firstError.focus();
        }
      }
    }, 100);
  }
}
